import { useRouter } from 'next/router'
import { ErrorBoundary } from 'react-error-boundary'
import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import clsx from 'clsx'

import { historyUtils } from '@wartek-id/toolbox-helper'
import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { CustomErrorFallback } from 'components/ErrorBoundary'
import {
  ForbiddenError,
  InternalServerError,
  ServiceUnavailableError,
  NotStartedError,
} from 'utils/customError'
import { USER_DETAIL_BASE_PATH } from '../constants/competency'

import type { FallbackProps } from 'react-error-boundary'

type RefleksiKompetensiErrorBoundaryProps = {
  children: React.ReactNode
  withHeader?: boolean
}

export function RefleksiKompetensiErrorBoundary({
  children,
  withHeader = false,
}: RefleksiKompetensiErrorBoundaryProps) {
  const router = useRouter()

  return (
    <ErrorBoundary
      resetKeys={[router.asPath]}
      FallbackComponent={({ error, resetErrorBoundary }) => {
        const Header = () => {
          return (
            withHeader && (
              <HeaderBar>
                <HeaderBarLeftAction
                  onClick={() => {
                    resetErrorBoundary()
                    historyUtils?.backToApp(router?.back)
                  }}
                />
                <HeaderBarTitle>Refleksi Kompetensi</HeaderBarTitle>
              </HeaderBar>
            )
          )
        }

        const ErrorStateWrapper = ({ children }) => {
          return (
            <div className="max-w-xl m-auto">
              <Header />
              {children}
            </div>
          )
        }

        if (error instanceof ForbiddenError) {
          return (
            <ErrorStateWrapper>
              <ForbiddenErrorFallback resetErrorBoundary={resetErrorBoundary} />
            </ErrorStateWrapper>
          )
        } else if (error instanceof NotStartedError) {
          return (
            <ErrorStateWrapper>
              <ServiceNotStartedErrorFallback />
            </ErrorStateWrapper>
          )
        } else if (
          error instanceof InternalServerError ||
          error instanceof ServiceUnavailableError
        ) {
          return (
            <ErrorStateWrapper>
              <ServiceFailedErrorFallback />
            </ErrorStateWrapper>
          )
        } else {
          return (
            // Workaround of horizontal scroll: add full screen height and overflow:hidden
            <div className="max-w-xl m-auto h-screen overflow-hidden">
              <Header />

              {/* Workaround of vertical centering: add negative margin */}
              <div className={clsx(withHeader && '-mt-4')}>
                <CustomErrorFallback
                  error={error}
                  resetErrorBoundary={resetErrorBoundary}
                />
              </div>
            </div>
          )
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

export default RefleksiKompetensiErrorBoundary

type ForbiddenErrorFallbackProps = Pick<FallbackProps, 'resetErrorBoundary'>

function ForbiddenErrorFallback({
  resetErrorBoundary,
}: ForbiddenErrorFallbackProps) {
  const router = useRouter()

  const queryClient = useQueryClient()

  useEffect(() => {
    // Invalidate the cache of the eligibiilty check query upon unmount
    return () => {
      queryClient.removeQueries(`${USER_DETAIL_BASE_PATH}/profile/REFLEKSI`)
    }
  }, [queryClient])

  return (
    <EmptyStateWithIllustration
      title="Anda KSPS tapi belum bisa akses? Tunggu ya, data Anda mungkin masih dalam proses input."
      description="Fitur ini tersedia untuk Kepala Sekolah dan Pengawas Sekolah terdaftar di Dapodik dan SIM Tendik."
      className="bg-gray-10"
    >
      <EmptyStateImage imageSrc="/images/errors/img-error-fixing.png" />
      <EmptyStateAction
        color="black"
        onClick={() => {
          resetErrorBoundary()
          historyUtils?.backToApp(() => router.replace('/home'))
        }}
      >
        Kembali ke Beranda
      </EmptyStateAction>
      <EmptyStateAction
        color="white"
        href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/21276770843289-Kendala-Akses-ke-Refleksi-Kompetensi"
        onClick={resetErrorBoundary}
      >
        Ke Pusat Bantuan
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}

function ServiceNotStartedErrorFallback() {
  const router = useRouter()

  return (
    <EmptyStateWithIllustration
      title="Halaman ini akan segera hadir untuk Anda"
      description="Saat ini Refleksi Kompetensi belum bisa diakses. Nantikan akses untuk semua, segera!"
      className="bg-gray-10"
    >
      <EmptyStateImage imageSrc="/images/errors/img-forbiden-access.png" />
      <EmptyStateAction
        color="black"
        onClick={() => {
          historyUtils?.backToApp(() => router.replace('/home'))
        }}
      >
        Kembali ke Beranda
      </EmptyStateAction>
      <EmptyStateAction
        color="white"
        href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/21276770843289?focus=true"
      >
        Ke Pusat Bantuan
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}

function ServiceFailedErrorFallback() {
  const router = useRouter()

  return (
    <EmptyStateWithIllustration
      title="Terjadi kendala sistem"
      description="Silakan coba lagi dalam beberapa saat."
      className="bg-gray-10"
    >
      <EmptyStateImage imageSrc="/images/errors/img-forbiden-access.png" />
      <EmptyStateAction
        color="black"
        onClick={() => {
          router.reload()
        }}
      >
        Coba lagi
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}
